import React, { useEffect, useState } from "react";
import { getViewportBucket, authStorageKeys } from "./portalHelpers";
import { stripPortalJwtFromStorage } from "./storage";
import PortalPoweredBy from "./PortalPoweredBy";
import PlanPaymentHold, { PaymentHoldProvider } from "./PlanPaymentHold";
import SubscriptionEndedNotice from "./SubscriptionEndedNotice";

const BILLING_PORTAL_IDS = new Set(["partner", "executive", "workspace", "engagement"]);

const PortalLayout = ({
  children,
  hideHeader = false,
  hideSidebar = false,
  dashboardOnly = false,
  portalId,
  Sidebar,
  Header,
  Breadcrumb,
  TrialPopup: Popup,
  StoreLocationCaptureModal,
}) => {
  const [viewport, setViewport] = useState(() => getViewportBucket());
  const [sidebarExpanded, setSidebarExpanded] = useState(() => getViewportBucket() === "desktop");
  const { poweredBy } = authStorageKeys(portalId);

  useEffect(() => {
    if (portalId) stripPortalJwtFromStorage(portalId);
  }, [portalId]);

  useEffect(() => {
    const handleResize = () => {
      const bucket = getViewportBucket();
      setViewport(bucket);
      if (bucket === "tablet" || bucket === "mobile") {
        setSidebarExpanded(false);
      } else if (bucket === "desktop") {
        setSidebarExpanded(true);
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const isCompact = viewport === "tablet" || viewport === "mobile";
  const sidebarOpen = !hideSidebar && sidebarExpanded && isCompact;

  const shell = (
    <div
      className={`portal-glass-shell portal-nye-theme app-layout flex h-screen overflow-hidden${isCompact ? " app-layout--compact" : ""}${
        sidebarOpen ? " sidebar-is-open" : ""
      }`}
    >
      {!hideSidebar && (
        <Sidebar
          dashboardOnly={dashboardOnly}
          expanded={sidebarExpanded}
          setExpanded={setSidebarExpanded}
        />
      )}
      <div
        className={`layout-content flex h-full w-full min-w-0 flex-col overflow-y-auto overflow-x-hidden transition-all duration-500 ${
          !hideSidebar ? (sidebarExpanded ? "layout-content--expanded" : "layout-content--collapsed") : ""
        }`}
      >
        <div className="layout-main flex min-h-full w-full min-w-0 flex-1 flex-col px-4 md:px-5 lg:px-6">
          {BILLING_PORTAL_IDS.has(portalId) ? <PlanPaymentHold portalId={portalId} /> : null}
          {!hideHeader && (
            <Header
              showLogo={hideSidebar}
              onToggleSidebar={
                !hideSidebar && isCompact ? () => setSidebarExpanded((open) => !open) : undefined
              }
              sidebarOpen={sidebarExpanded}
            />
          )}
          {!hideSidebar && Breadcrumb ? (
            <div className="layout-breadcrumb pt-3 md:pt-4">
              <Breadcrumb />
            </div>
          ) : null}
          <div className="page-shell flex flex-1 flex-col pb-5 pt-4 md:pb-6 md:pt-5">{children}</div>
          {localStorage.getItem(poweredBy) !== "0" ? <PortalPoweredBy /> : null}
        </div>
      </div>

      {Popup ? <Popup /> : null}
      {portalId === "executive" || portalId === "workspace" || portalId === "partner" ? <SubscriptionEndedNotice /> : null}
      {!hideSidebar && StoreLocationCaptureModal ? <StoreLocationCaptureModal /> : null}
    </div>
  );

  if (!BILLING_PORTAL_IDS.has(portalId)) return shell;
  return <PaymentHoldProvider>{shell}</PaymentHoldProvider>;
};

export default PortalLayout;
